import { Auth, statuses } from "@icg360/ipc-auth-js";
import * as Fullstory from "@fullstory/browser";
import { TrackJS } from "trackjs";

import AP from "./legacy/global";
import { getOrganization } from "./promises/get-organization";
import { getTerms } from "./promises/get-terms";
import { handleIXDirectoryAuth } from "./promises/handle-ixdirectory-auth";
import handlePXServerAuth from "./promises/handle-pxserver-auth";
import handleSAMLPassthrough from "./promises/handle-saml-passthrough";
import { loadEnvInfo } from "./store/env-info";
import { initFeatureFlags } from "./store/feature-flags";
import { pickEnvInfo } from "./store/selectors";
import setupGoogleMapAPI from "./units/setup-google-map-api";
import * as tracking from "./units/tracking";
import { logout } from "./utils/creds";
import { getApiUrl } from "./utils/get-api-url";
import userDataFromAppSettings from "./utils/pxserver-settings-parser";
import storage from "./utils/storage";
import parseIdentity from "./utils/xml-parsers/identity";

const parser = new DOMParser();

export default async function (store, envInfo, updateStatus, reporter) {
  // Migration Plan: Load config into AP.config the way we did before (config.{env}.json files)
  // But also load the new api service routes into AP.config.api
  // Eventually this will likely change as we refactor the config files

  // eslint-disable-next-line no-undef
  const config = CONFIG;
  AP.config = config;

  const isProduction = config?.envConfig?.environmentName === "production";

  // Either the b64 cookie or the authString cookie (SAML) has to be set,
  // otherwise we need to send the user to the login screen
  if ((!envInfo.b64 && !envInfo.authString) || isInactive()) {
    logout();
    return;
  }

  // Use ixAuth to check if cookie is valid otherwise logout
  const client = new Auth({
    servicesUrl: getApiUrl("ixauth")
  });
  const auth = await client.identify();
  if (auth.status !== statuses.success) {
    reporter(auth.message);
    logout();
    return;
  }
  updateStatus("Loading user...");
  // Next Todo: Replace `getUserDetails` and `parseUserData` with just
  // user data returned from ixAuth
  const {
    pxServerUser,
    identity,
    loginType = ""
  } = await getUserDetails({
    config,
    envInfo
  });

  const userData = parseUserData({
    pxServerUser,
    identity,
    env: config.envConfig.environmentName,
    organizationID: config.appConfig.application.organizationId
  });

  // StackAdapt Pixel - fired on app load after auth
  if (typeof window.saq !== "undefined") {
    window.saq("ts", "5Bxj83jOPx3rxc5WDqiPDA");
  }

  // Load org data, terms, and Google API code.
  // All of these should fallback safely
  const [org, terms] = await Promise.all([
    userData.alc
      ? getOrganization({
          alc: userData.alc
        }).catch(() => {
          reporter("Failed to load organization data.");
          return {};
        })
      : {},
    getTerms().catch(() => {
      reporter("Failed to load vocab terms.");
      return {};
    }),
    setupGoogleMapAPI(config.envConfig.google.maps.apiKey)
  ]);

  store.dispatch(
    loadEnvInfo({
      ...envInfo,
      ...userData,
      // Using a separate name to denote roles from the Auth service instead of pxserver
      // Soon we need to switch all roles to the roles from Auth
      authRoles: auth.identity.roles,
      loginType,
      org: {
        agencyLocationCode: org.role?.dataItems.agencyLocationCode || "",
        pupStatus: org.role?.dataItems.producerProgramStatus || "",
        perks: org.role?.dataItems.Perks || [],
        physicalStateCode:
          org.addresses?.find(addr => addr.type === "physical").Province || ""
      }
    })
  );

  Fullstory.init({
    orgId: "XMPRW",
    devMode: !isProduction
  });
  Fullstory.identify(userData.username, {
    email: userData.email,
    displayName: userData.fullname,
    fullname_str: userData.fullname,
    organizationId_str: config.appConfig.application.organizationId,
    programId_str: envInfo.programID,
    alc_str: userData.alc
  });

  const shouldEnableTrackjs =
    !/(agentportal\.dev|stage-sagesure|localhost)/.test(window.location.host);
  const shouldTrack = document.cookie.indexOf("DoNotTrack=true") < 0;
  if (shouldTrack && shouldEnableTrackjs) {
    TrackJS.install({
      token: "76bc2e107fcd4007bdf4b17654b7d041",
      application: "agent_portal",
      version: CONFIG.version.label,
      onError: payload => {
        const isFullstoryInitialized = !!window._fs_initialized;
        const isFullstoryInDevMode = !!window._fs_dev_mode;
        if (isFullstoryInitialized && !isFullstoryInDevMode) {
          payload.metadata.push({
            key: "fullstorySessionURL",
            value: Fullstory.getCurrentSessionURL(true)
          });
        }
        return true;
      }
    });
  }

  // Legacy, set user info to AP object
  Object.assign(AP, envInfo, {
    vocab: terms,
    pxServerUser,
    identity: parser.parseFromString(identity, "text/xml"),
    u: userData.username,
    uname: userData.fullname,
    email: userData.email,
    agencyLocationId: userData.alc || null,
    isAdmin: userData.isAdmin,
    isBackoffice: userData.isBackoffice,
    adminModeActive: false,
    doLogout: logout,
    getAuthString: createGetAuthString(envInfo),
    getEventTarget
  });

  const state = store.getState();

  // Setup tracking
  tracking.init({
    ...config.envConfig.mixpanel,
    enabled: !!config.envConfig.mixpanel?.enabled
  });
  tracking.configureUser(pickEnvInfo(state));

  updateStatus("Loading app...");
  return Promise.all([
    import(/* webpackChunkName: "app" */ "./portal-app"),
    // Feature flags couldn't start sooner because it requires org data.
    config.envConfig.launchDarklyID &&
      store
        .dispatch(initFeatureFlags(config.envConfig.launchDarklyID))
        .catch(e => {
          reporter("Launch Darkly failed to get feature flags");
          reporter(e);
          return;
        })
  ]).then(([app]) => app);
}

function getUserDetails({ config, envInfo }) {
  const username = window.atob(envInfo.b64).split(":")[0];
  if (envInfo.agentToken) {
    return handleSAMLPassthrough(envInfo.authString).catch(error => {
      alert("MXHR FAILURE - BAD AUTH-SESSION-URI DATA\n" + error.message);
    });
  }
  return Promise.all([
    handlePXServerAuth(username, envInfo.b64, envInfo.programID, config),
    handleIXDirectoryAuth(username, envInfo.b64)
  ]).then(([pxServerUser, identity]) => ({
    pxServerUser,
    identity
  }));
}

function isInactive() {
  const storedLastActive = storage.getItem("last-active");
  if (storedLastActive) {
    const lastActive = parseInt(storedLastActive, 10);
    // Is the difference between now and our last active session greater than 6 hours
    if (new Date().getTime() - lastActive > 6 * 60 * 60 * 1000) {
      return true;
    }
  }
  return false;
}

function createGetAuthString(envInfo) {
  return () =>
    envInfo.agentToken != null
      ? `AgentToken ${envInfo.agentToken}`
      : `Basic ${envInfo.b64}`;
}

function getEventTarget(e) {
  var target;
  if (!e) e = window.event;
  if (e.target) target = e.target;
  else if (e.srcElement) target = e.srcElement;
  //if safari
  if (target.nodeType == 3) target = target.parentNode;
  return target;
}

function selectRoles(pxServerUser) {
  let roles = [];

  pxServerUser.querySelectorAll("role").forEach(role => {
    roles.push((role.id || "").toUpperCase());
  });

  return roles;
}
function selectIsLocalAdmin(pxServerUser) {
  return !!pxServerUser.querySelector(
    'right[softwareapplication="pxserver"][resource="users"][segment="agency-own"][action="manage"][access="grant"]'
  );
}

function parseUserData({ pxServerUser, identity, env, organizationID }) {
  const {
    attrs: { id: username },
    data: { channel = "", phoneNumber = "" },
    Name: fullname,
    Email: email,
    affiliations,
    applicationSettings
  } = parseIdentity(identity);

  const { target: alc = "", targetName: affiliation = "" } =
    affiliations.find(({ type }) => type === "agent_location") || {};
  const roles = rolesFromAppSettings({
    applicationSettings,
    env,
    organizationID
  });

  return {
    alc,
    affiliation,
    channel,
    email,
    fullname,
    isLocalAdmin: selectIsLocalAdmin(pxServerUser),
    isAdmin: roles.includes("CONTENT_ADMIN"),
    isBackoffice: roles.includes("backoffice"),
    phone: phoneNumber,
    roles: selectRoles(pxServerUser),
    username,
    ...userDataFromAppSettings(applicationSettings) // returns {dateAppointed, isLicensee, isPrimaryContact, isPrincipal, licenseNumber}
  };
}
function rolesFromAppSettings({ applicationSettings, env, organizationID }) {
  const appSettings = applicationSettings.find(
    ({ applicationName, organizationId, environmentName }) =>
      applicationName === "agentportal" &&
      organizationId === organizationID &&
      environmentName === env
  );
  if (!appSettings || !appSettings.children) return "";
  const roles = appSettings.children.find(({ name }) => name === "roles");
  return roles && roles.text ? roles.text : "";
}
